import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  Inject,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';

import { Store } from '@ngrx/store';

import { UploadService } from '../../../@services/upload.service';
import { OrdersActions } from '../../../@store/orders/orders.actions';
import {
  selectCreatedOrder,
  selectCreatedOrderError,
  selectCreatedOrderSuccess,
} from '../../../@store/orders/orders.selectors';
import { SafeAny } from '../../../types/safe-any';

@Component({
  selector: 'ciwc-approve-quote-for-production',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinner,
  ],
  templateUrl: './approve-quote-for-production.component.html',
  styleUrls: ['./approve-quote-for-production.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApproveQuoteForProductionComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogData = inject<SafeAny>(MAT_DIALOG_DATA);
  private readonly store = inject(Store);
  private readonly uploadService = inject(UploadService);
  readonly createdOrderSuccess = this.store.selectSignal(selectCreatedOrderSuccess);
  readonly createdOrderError = this.store.selectSignal(selectCreatedOrderError);
  readonly createdOrder = this.store.selectSignal(selectCreatedOrder);

  private unit = 'EA';
  private pricePerFeet = 0;

  approveForm!: FormGroup;
  isUploading = signal(false);
  uploadError = signal('');

  // Create quantity options from the quantity breaks
  quantityOptions = this.dialogData.tabsValues;
  hasPieces = this.dialogData.hasPieces;

  weeksOptions = [
    ...Array.from({ length: 52 }, (_, i) => ({
      value: String(i + 1),
      label: `${i + 1} ${i + 1 === 1 ? 'Week' : 'Weeks'}`,
    })),
  ];

  isCreatingOrder = signal(false);

  constructor(
    public dialogRef: MatDialogRef<ApproveQuoteForProductionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SafeAny,
  ) {
    effect(() => {
      // todo - temporary console logs
      console.log('createdOrderSuccess:', this.createdOrderSuccess());
      console.log('createdOrderError:', this.createdOrderError());
      console.log('createdOrder:', this.createdOrder());
      console.log('isCreatingOrder:', this.isCreatingOrder());

      if (this.isCreatingOrder()) {
        if (this.createdOrderSuccess()) {
          this.dialogRef.close(this.createdOrder()?.id);
        }

        if (this.createdOrderError()) {
          this.isCreatingOrder.set(false);
        }
      }
    });
  }

  ngOnInit(): void {
    this.approveForm = this.fb.group({
      clientPurchaseOrder: ['', Validators.required],
      totalQuantity: ['', Validators.required],
      weeksAmount: ['', Validators.required],
      purchaseOrderDocument: ['', Validators.required],
    });

    this.listenToTotalQuantityChanges();
  }

  get purchaseOrderDocument() {
    return this.approveForm.get('purchaseOrderDocument');
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    this.isCreatingOrder = signal(true);

    const formValues = this.approveForm.value;

    this.store.dispatch(
      OrdersActions.createOrder({
        payload: {
          ...formValues,
          unit: this.unit,
          pricePerUnit: this.pricePerFeet,
          quoteId: this.dialogData.quoteId,
          totalQuantity: formValues.totalQuantity.quantityBreak,
        },
      }),
    );
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.isUploading.set(true);
      this.uploadError.set('');

      this.uploadService.uploadFile(file).subscribe({
        next: (res) => {
          this.isUploading.set(false);
          this.uploadError.set('');
          this.purchaseOrderDocument?.patchValue(res.name);
        },
        error: (_err) => {
          this.isUploading.set(false);
          this.uploadError.set('Failed to upload file. Please try again.');
        },
      });
    }
  }

  private listenToTotalQuantityChanges(): void {
    // Add listener for totalQuantity changes
    this.approveForm.get('totalQuantity')?.valueChanges.subscribe((value) => {
      if (this.hasPieces) {
        this.pricePerFeet = value.pricePerEA;
      } else {
        this.unit = 'ft';
        this.pricePerFeet = value.quantityBreak ? value.pricePerFeet : 0;
      }
    });
  }
}
